import { useMemo } from "react"
import {
  useExternalStoreRuntime,
  type ThreadMessageLike,
  type AppendMessage,
} from "@assistant-ui/react"
import { useAppSelector } from "@/reducers/hooks"
import { selectMessagesFromChat } from "@/reducers/messagesSliceSelectors"
import { selectRttText } from "@/reducers/rttSlice"
import {
  selectFinishedTurns,
  selectLiveTurn,
  type FinishedTurn,
} from "@/reducers/agentPipelineSlice"
import type { Chat } from "@/reducers/chatsSlice"
import type { Message } from "@/reducers/messagesSlice"
import ChatHelpers from "@/lib/chatHelpers"
import type { AIChatMessageMeta } from "./aiChatMessageMeta"

/**
 * Convert a Redux Message to assistant-ui's ThreadMessageLike format.
 * Exported for testing.
 */
export function convertMessage(
  message: Message,
  summary?: FinishedTurn,
): ThreadMessageLike {
  const isAI = ChatHelpers.isMessageFromSlickAI(message.from)
  const meta: AIChatMessageMeta = {
    ...(message.aiWidgetMap && { aiWidgetMap: message.aiWidgetMap }),
    ...(message.aiWidgetTacho && { aiWidgetTacho: message.aiWidgetTacho }),
    ...(message.aiWidgetOrder && { aiWidgetOrder: message.aiWidgetOrder }),
    ...(summary && { summary }),
  }

  return {
    role: isAI ? "assistant" : "user",
    content: isAI
      ? [{ type: "text", text: message.body }]
      : userContent(message),
    id: message.id,
    createdAt: new Date(message.timestamp),
    ...(isAI && { status: { type: "complete" as const, reason: "stop" } }),
    metadata: { custom: meta },
  }
}

const IMAGE_FILE = /\.(png|jpe?g|gif|webp)$/i

/**
 * A file message's body is the caption the dispatcher typed with it, or the
 * bare url when there was none. Showing the body alone would leave the bubble
 * with just the caption, or a raw link, and no sign of the file. Any other
 * file keeps its link in the text, where AIChatUserText shows it as a file.
 */
function userContent(message: Message): ThreadMessageLike["content"] {
  if (!message.file || !message.url) {
    return [{ type: "text", text: message.body }]
  }
  const caption = message.body.trim() === message.url ? "" : message.body
  if (!IMAGE_FILE.test(message.fileName ?? message.url)) {
    return [
      {
        type: "text",
        text: caption ? `${message.url}\n${caption}` : message.body,
      },
    ]
  }

  return [
    { type: "image", image: message.url! },
    ...(caption ? [{ type: "text" as const, text: caption }] : []),
  ]
}

/**
 * Hook that bridges Redux state → assistant-ui ExternalStoreRuntime.
 *
 * Reads messages from messagesSlice and appends one synthetic assistant
 * message for the live agent turn, carrying the streamed RTT text, the turn
 * start and its steps.
 */
export function useAIChatRuntime(
  chat: Chat,
  onSendMessage: (text: string) => void,
) {
  const messagesSelector = useMemo(
    () => selectMessagesFromChat(chat.jid),
    [chat.jid],
  )
  const reduxMessages = useAppSelector(messagesSelector)
  const rttText = useAppSelector((state) => selectRttText(state, chat.jid))
  const liveTurn = useAppSelector((state) => selectLiveTurn(state, chat.jid))
  const finishedTurns = useAppSelector(selectFinishedTurns)

  const threadMessages = useMemo(() => {
    const converted = (reduxMessages ?? []).map((message) =>
      convertMessage(message, finishedTurns[message.id]),
    )

    // One id for the whole turn, so the status row keeps its expanded state
    // when the answer starts streaming under it.
    if (liveTurn) {
      const meta: AIChatMessageMeta = { turn: liveTurn }
      converted.push({
        role: "assistant",
        content: [{ type: "text", text: rttText ?? "" }],
        id: "agent-turn",
        status: { type: "running" },
        metadata: { custom: meta },
      })
    }

    return converted
  }, [reduxMessages, finishedTurns, rttText, liveTurn])

  const onNew = async (message: AppendMessage) => {
    if (message.role !== "user") return
    const parts = message.content as ReadonlyArray<{
      type: string
      text?: string
    }>
    const textPart = parts.find((c) => c.type === "text")
    if (textPart?.text != null) {
      onSendMessage(textPart.text)
    }
  }

  return useExternalStoreRuntime({
    messages: threadMessages,
    isRunning: !!liveTurn,
    onNew,
    convertMessage: (msg: ThreadMessageLike) => msg,
  })
}
